import React, { useEffect, useState } from 'react';
import { Banknote, Filter, Gift, Wallet } from 'lucide-react';
import client from '../../lib/api';
import { TURNOVER_BADGE, turnoverView } from '../lib/principalTurnover';

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} ₺`;

// Sunucu zamanları UTC'dir; panel Türkiye saatini gösterir (tarih süzgeciyle
// aynı sabit: backend `TR_UTC_OFFSET`).
const TR_OFFSET_MS = 3 * 60 * 60 * 1000;

const trParts = (value) => {
  if (!value) return null;
  const raw = String(value);
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : `${raw}Z`);
  if (Number.isNaN(date.getTime())) return null;
  const tr = new Date(date.getTime() + TR_OFFSET_MS);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${tr.getUTCFullYear()}-${pad(tr.getUTCMonth() + 1)}-${pad(tr.getUTCDate())}`,
    time: `${pad(tr.getUTCHours())}:${pad(tr.getUTCMinutes())}`,
    label: `${pad(tr.getUTCDate())}.${pad(tr.getUTCMonth() + 1)}.${tr.getUTCFullYear()} ${pad(tr.getUTCHours())}:${pad(tr.getUTCMinutes())}`,
  };
};

// `type=withdraw` satırlarının hepsi oyuncuya ödeme değildir: yöneticinin
// bakiye kesintisi ve bonus iptali de bu türle yazılır (backend
// `admin_finance.MANUAL_DEBIT_METHOD` / `BONUS_CANCEL_METHOD`).
const NON_PAYOUT_WITHDRAW_METHODS = new Set(['Admin Düşme', 'Bonus İptali']);

const WITHDRAW_STATUS = {
  onaylandi: { label: 'Onaylandı', className: 'badge-green' },
  reddedildi: { label: 'Reddedildi', className: 'badge-red' },
  beklemede: { label: 'Beklemede', className: 'badge-yellow' },
};

const CLAIM_STATUS = {
  active: { label: 'Çevrimde', className: 'badge-yellow' },
  completed: { label: 'Tamamlandı', className: 'badge-green' },
  cancelled: { label: 'İptal', className: 'badge-red' },
  expired: { label: 'Süresi doldu', className: 'badge-gray' },
  lost: { label: 'Bakiye bitti', className: 'badge-gray' },
};

/**
 * Bahis geçmişinin üstündeki çevrim denetimi kartı: üyenin **son yatırımı**
 * (ve o andan sonraki bahis toplamı), **son çekim talebi** ve **aldığı son
 * bonuslar**.
 *
 * Çekim kararında sorulan soru "bu bahisler hangi yatırımın/bonusun
 * parasıyla oynandı"dır; bahis listesi yalnızca turları gösterdiği için
 * yönetici yatırımı ve bonusu ayrı ekranlarda arıyordu. Veri mevcut iki
 * uçtan gelir (`/turnover`, `/bonus-claims`); "Bu yatırımdan sonrasını
 * göster" düğmesi tarih süzgecini yatırımın dakikasına kurar.
 */
const WagerCheckPanel = ({ userId, onFilterSince }) => {
  const [turnover, setTurnover] = useState(null);
  const [claims, setClaims] = useState([]);
  const [withdrawal, setWithdrawal] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      client.get(`/admin/users/${userId}/turnover`),
      client.get(`/admin/users/${userId}/bonus-claims`, { params: { limit: 5 } }),
      // Çekim kartı ek bilgidir: okunamazsa diğer iki kart yine çizilir.
      client.get(`/admin/users/${userId}/transactions`, {
        params: { group: 'withdraw', limit: 10 },
      }).catch(() => null),
    ])
      .then(([turnoverResponse, claimsResponse, withdrawResponse]) => {
        if (cancelled) return;
        setTurnover(turnoverResponse.data || null);
        setClaims((claimsResponse.data?.items || []).slice(0, 5));
        setWithdrawal((withdrawResponse?.data?.items || []).find(
          (item) => !NON_PAYOUT_WITHDRAW_METHODS.has(item.method),
        ) || null);
        setFailed(false);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [userId]);

  if (failed) return null;
  if (!turnover) return null;

  const view = turnoverView(turnover);
  const deposit = trParts(turnover.deposit_at);
  const withdrawStatus = withdrawal
    ? (WITHDRAW_STATUS[withdrawal.status] || WITHDRAW_STATUS.beklemede)
    : null;

  return (
    <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]" data-testid="wager-check">
      <div className="admin-card p-4">
        <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <Wallet size={13} className="text-emerald-400" /> Son Yatırım
          <span className={`badge-pill ml-auto ${TURNOVER_BADGE[view.tone] || 'badge-gray'}`}>{view.label}</span>
        </div>
        {turnover.has_deposit ? (
          <>
            <div className="text-lg font-black text-white">{money(turnover.deposit_amount)}</div>
            <div className="mt-0.5 text-xs text-slate-400">
              {deposit?.label || '—'}
              {turnover.deposit_method ? ` • ${turnover.deposit_method}` : ''}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-slate-500">Sonrasındaki bahis</div>
                <div className="font-bold text-white">{money(turnover.total_bet)}</div>
              </div>
              <div>
                <div className="text-slate-500">Eksik çevrim</div>
                <div className={`font-bold ${Number(turnover.remaining) > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {money(turnover.remaining)}
                </div>
              </div>
            </div>
            {deposit && onFilterSince && (
              <button
                type="button"
                onClick={() => onFilterSince(deposit)}
                className="admin-btn-secondary mt-3 inline-flex items-center gap-1.5 text-xs"
              >
                <Filter size={12} /> Bu yatırımdan sonrasını göster
              </button>
            )}
          </>
        ) : (
          <p className="text-xs text-slate-400">{view.detail}</p>
        )}
      </div>

      <div className="admin-card p-4">
        <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <Banknote size={13} className="text-rose-400" /> Son Çekim
          {withdrawStatus && <span className={`badge-pill ml-auto ${withdrawStatus.className}`}>{withdrawStatus.label}</span>}
        </div>
        {withdrawal ? (
          <>
            <div className="text-lg font-black text-white">{money(Math.abs(Number(withdrawal.amount || 0)))}</div>
            <div className="mt-0.5 text-xs text-slate-400">
              {trParts(withdrawal.created_at)?.label || '—'}
              {withdrawal.method ? ` • ${withdrawal.method}` : ''}
            </div>
            {withdrawal.balance_after !== null && withdrawal.balance_after !== undefined && (
              <div className="mt-3 text-xs">
                <div className="text-slate-500">Çekim sonrası bakiye</div>
                <div className="font-bold text-white">{money(withdrawal.balance_after)}</div>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-slate-400">Üyenin çekim talebi yok.</p>
        )}
      </div>

      <div className="admin-card p-4 lg:col-span-2 2xl:col-span-1">
        <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <Gift size={13} className="text-amber-400" /> Aldığı Bonuslar
          <span className="ml-auto normal-case tracking-normal text-slate-500">son {claims.length}</span>
        </div>
        {claims.length === 0 ? (
          <p className="text-xs text-slate-400">Üyenin bonus kaydı yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-1.5 pr-2">Bonus</th>
                  <th className="py-1.5 pr-2 text-right">Tutar</th>
                  <th className="py-1.5 pr-2 text-right">Çevrim</th>
                  <th className="py-1.5 pr-2">Durum</th>
                  <th className="py-1.5 text-right">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => {
                  const status = CLAIM_STATUS[claim.status] || CLAIM_STATUS.completed;
                  const spins = Number(claim.freespin_count || 0);
                  return (
                    <tr key={`${claim.source}-${claim.id}`} className="border-t border-white/5">
                      <td className="py-1.5 pr-2 font-semibold text-slate-200">{claim.bonus_title || '—'}</td>
                      <td className="py-1.5 pr-2 text-right text-white">
                        {Number(claim.amount || 0) > 0 ? money(claim.amount) : (spins > 0 ? `${spins} FS` : '—')}
                      </td>
                      <td className="py-1.5 pr-2 text-right text-slate-300">
                        {Number(claim.wager_required || 0) > 0
                          ? `${money(claim.wager_completed)} / ${money(claim.wager_required)}`
                          : '—'}
                      </td>
                      <td className="py-1.5 pr-2"><span className={`badge-pill ${status.className}`}>{status.label}</span></td>
                      <td className="py-1.5 text-right text-slate-400">{trParts(claim.created_at)?.label || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WagerCheckPanel;
